import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Info, Users, BookOpen, Calendar, TrendingUp, ArrowRight } from 'lucide-react';

const InfoTip = ({ text }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button
        type="button"
        className="inline-flex items-center justify-center w-4 h-4 rounded-full text-muted-foreground hover:text-foreground transition-colors focus:outline-none"
        aria-label="More information"
      >
        <Info className="w-3 h-3" />
      </button>
    </TooltipTrigger>
    <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
      {text}
    </TooltipContent>
  </Tooltip>
);

const TIPS = {
  total:       'Total number of applications received. Use the intake filter to narrow by a specific cohort cycle.',
  eligible:    'Applicants who meet all three hard criteria: age ≤25, A-Level Division 1, and LLB GPA ≥3.3.',
  acceptance:  'Percentage of eligible applicants who were accepted (Accepted ÷ Eligible × 100).',
  students:    'Students currently enrolled across all cohorts.',
  cohorts:     'Cohort groups currently marked as active.',
  funnel:      'Shows how applicants move through each stage of the admissions pipeline.',
  statusBar:   'Visual distribution of all applications by current status.',
  recent:      'The 5 most recently submitted applications, across all intakes.',
};

const pct = (num, den) => (den > 0 ? `${Math.round((num / den) * 100)}%` : '—');

const DashboardTab = ({
  applications,
  students,
  cohorts,
  intakePeriods,
  selectedIntakeId,
  onIntakeChange,
  onNavigateToApps,
  canSeeApps = true,
}) => {
  const navigate = useNavigate();

  const total          = applications.length;
  const eligible       = applications.filter(a => a.eligible).length;
  const ineligible     = applications.filter(a => !a.eligible).length;
  const enrolled       = applications.filter(a => a.status === 'enrolled').length;
  const notShortlisted = applications.filter(a => a.status === 'not_shortlisted').length;
  const shortlisted    = applications.filter(a => a.status === 'shortlisted').length;
  const testInvited    = applications.filter(a => a.status === 'test_invited').length;
  const testCompleted  = applications.filter(a => a.status === 'test_completed').length;
  const withdrawn      = applications.filter(a => a.status === 'withdrawn').length;
  // Legacy status support
  const accepted       = enrolled  + applications.filter(a => a.status === 'accepted').length;
  const rejected       = notShortlisted + applications.filter(a => a.status === 'rejected').length;

  const activeStudents = students.length;
  const activeCohorts  = cohorts.filter(c => c.active).length;

  // KPI cards — split into admissions-only and always-visible
  const admissionsKpis = [
    {
      label: 'Total Applications',
      value: total,
      sub: selectedIntakeId === 'all' ? 'All intakes' : 'This intake',
      icon: <Users className="w-5 h-5" />,
      color: 'text-primary',
      tip: TIPS.total,
    },
    {
      label: 'Eligible',
      value: eligible,
      sub: `${pct(eligible, total)} of total`,
      icon: <Users className="w-5 h-5" />,
      color: 'text-green-600',
      tip: TIPS.eligible,
    },
    {
      label: 'Acceptance Rate',
      value: pct(accepted, eligible),
      sub: `${enrolled} of ${eligible} eligible`,
      icon: <TrendingUp className="w-5 h-5" />,
      color: 'text-blue-600',
      tip: TIPS.acceptance,
    },
  ];

  const academyKpis = [
    {
      label: 'Active Students',
      value: activeStudents,
      sub: 'Across all cohorts',
      icon: <BookOpen className="w-5 h-5" />,
      color: 'text-purple-600',
      tip: TIPS.students,
    },
    {
      label: 'Active Cohorts',
      value: activeCohorts,
      sub: `${cohorts.length} total`,
      icon: <Calendar className="w-5 h-5" />,
      color: 'text-orange-600',
      tip: TIPS.cohorts,
    },
  ];

  const visibleKpis = canSeeApps ? [...admissionsKpis, ...academyKpis] : academyKpis;

  const statusGroups = [
    { label: 'Test Invited',    count: testInvited,    color: 'bg-blue-400'   },
    { label: 'Test Completed',  count: testCompleted,  color: 'bg-purple-400' },
    { label: 'Shortlisted',     count: shortlisted,    color: 'bg-yellow-400' },
    { label: 'Enrolled',        count: enrolled,       color: 'bg-green-500'  },
    { label: 'Not Shortlisted', count: notShortlisted, color: 'bg-red-400'    },
    { label: 'Withdrawn',       count: withdrawn,      color: 'bg-gray-400'   },
    { label: 'Ineligible',      count: ineligible,     color: 'bg-gray-300'   },
  ];

  const inAssessment = testInvited + testCompleted;
  const funnelStages = [
    { label: 'Submitted',    count: total,          barColor: 'bg-gray-400',   desc: '100% of submitted' },
    { label: 'Eligible',     count: eligible,       barColor: 'bg-blue-400',   desc: `${pct(eligible, total)} of submitted` },
    { label: 'Assessment',   count: inAssessment,   barColor: 'bg-purple-400', desc: `${pct(inAssessment, eligible)} of eligible` },
    { label: 'Shortlisted',  count: shortlisted,    barColor: 'bg-yellow-400', desc: `${pct(shortlisted, eligible)} of eligible` },
    { label: 'Enrolled',     count: enrolled,       barColor: 'bg-green-500',  desc: `${pct(enrolled, eligible)} of eligible` },
  ];

  const recent = [...applications]
    .sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at))
    .slice(0, 5);

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-6">

        {/* Intake filter — admissions only */}
        {canSeeApps && (
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm font-medium text-muted-foreground">Showing data for:</span>
            <select
              className="border rounded px-3 py-1.5 text-sm bg-white"
              value={selectedIntakeId}
              onChange={e => onIntakeChange(e.target.value)}
            >
              <option value="all">All Intakes</option>
              {intakePeriods.map(ip => (
                <option key={ip.id} value={ip.id}>{ip.name}</option>
              ))}
            </select>
            {selectedIntakeId !== 'all' && (
              <>
                <button
                  type="button"
                  onClick={() => onIntakeChange('all')}
                  className="text-xs text-muted-foreground underline hover:text-foreground"
                >
                  Clear filter
                </button>
                <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                  Applications tab is also filtered to this intake
                </span>
              </>
            )}
          </div>
        )}

        {/* KPI cards */}
        <div className={`grid gap-4 ${canSeeApps ? 'grid-cols-2 md:grid-cols-5' : 'grid-cols-2'}`}>
          {visibleKpis.map(({ label, value, sub, icon, color, tip }) => (
            <Card key={label} className="relative">
              <CardHeader className="pb-1 pt-4 px-4">
                <div className="flex items-center justify-between mb-1">
                  <CardDescription className="text-xs font-medium">{label}</CardDescription>
                  <InfoTip text={tip} />
                </div>
                <CardTitle className={`text-2xl font-bold ${color}`}>{value}</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{sub}</span>
                  <span className={`${color} opacity-25`}>{icon}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Cohort quick-access for non-admissions users */}
        {!canSeeApps && cohorts.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Cohorts</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="divide-y">
                {cohorts.map(cohort => (
                  <div key={cohort.id} className="flex items-center justify-between py-3 text-sm">
                    <div>
                      <p className="font-medium">{cohort.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {cohort.active ? 'Active' : 'Inactive'}
                        {cohort.start_date ? ` · Started ${cohort.start_date}` : ''}
                      </p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => navigate('/admin/cohorts')}>
                      Manage <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Admissions sections */}
        {canSeeApps && (
          <>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Application Funnel */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-base">Application Funnel</CardTitle>
                    <InfoTip text={TIPS.funnel} />
                  </div>
                </CardHeader>
                <CardContent>
                  {total === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-6">No applications yet.</p>
                  ) : (
                    <div className="space-y-3">
                      {funnelStages.map(({ label, count, barColor, desc }) => (
                        <div key={label} className="flex items-center gap-3">
                          <div className="w-24 text-xs text-right text-muted-foreground shrink-0">{label}</div>
                          <div className="flex-1 bg-gray-100 rounded-full h-6 relative overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all duration-500 ${barColor}`}
                              style={{ width: `${total > 0 ? (count / total) * 100 : 0}%`, minWidth: count > 0 ? '1.5rem' : 0 }}
                            />
                          </div>
                          <div className="w-6 text-xs font-bold text-right shrink-0">{count}</div>
                          <div className="w-36 text-xs text-muted-foreground shrink-0 hidden lg:block">{desc}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Status Breakdown */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-base">Status Breakdown</CardTitle>
                    <InfoTip text={TIPS.statusBar} />
                  </div>
                </CardHeader>
                <CardContent>
                  {total === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-6">No applications yet.</p>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex h-9 rounded-lg overflow-hidden gap-px">
                        {statusGroups.filter(g => g.count > 0).map(g => (
                          <div
                            key={g.label}
                            className={`${g.color} transition-all duration-500`}
                            style={{ width: `${(g.count / total) * 100}%` }}
                            title={`${g.label}: ${g.count} (${pct(g.count, total)})`}
                          />
                        ))}
                      </div>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                        {statusGroups.map(g => (
                          <div key={g.label} className="flex items-center gap-2 text-xs">
                            <div className={`w-2.5 h-2.5 rounded-sm shrink-0 ${g.color}`} />
                            <span className="text-muted-foreground flex-1">{g.label}</span>
                            <span className="font-semibold">{g.count}</span>
                            <span className="text-muted-foreground">{pct(g.count, total)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Recent Applications */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-base">Recent Applications</CardTitle>
                    <InfoTip text={TIPS.recent} />
                  </div>
                  <Button variant="outline" size="sm" onClick={onNavigateToApps}>
                    View All <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {recent.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">No applications yet.</p>
                ) : (
                  <div className="divide-y">
                    {recent.map(app => (
                      <div key={app.id} className="flex items-center justify-between py-3 text-sm">
                        <div className="min-w-0 mr-4">
                          <div className="font-medium truncate">{app.full_name}</div>
                          <div className="text-xs text-muted-foreground truncate">{app.email}</div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {app.score != null ? (
                            <span className={`text-xs font-bold ${
                              app.score >= 70 ? 'text-green-600' :
                              app.score >= 50 ? 'text-yellow-600' : 'text-red-500'
                            }`}>
                              {app.score.toFixed(1)}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            app.status === 'accepted' ? 'bg-green-100 text-green-800' :
                            app.status === 'rejected' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {app.status}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            app.eligible ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-500'
                          }`}>
                            {app.eligible ? 'Eligible' : 'Ineligible'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

      </div>
    </TooltipProvider>
  );
};

export default DashboardTab;
